import React, { useEffect, useState } from "react";
import { api, formatError } from "@/api/client";
import { Plus, X, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

const MODULES = ["finance", "business", "users"];
const PERM_ACTIONS = {
  finance: ["view", "add", "edit", "delete", "approve"],
  business: ["view", "add", "edit", "delete"],
  users: ["view", "add", "edit", "delete"],
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null); // null | 'new' | userObj
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    const ok = await confirm({ title: "Delete this user?", message: "This is permanent and cannot be undone.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Team</div>
          <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">People & Permissions</h1>
        </div>
        <button data-testid="new-user-btn" onClick={() => setOpen("new")} className="dos-btn dos-btn-primary"><Plus size={14} /> Add Person</button>
      </div>

      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                <td className="px-4 py-3 font-medium text-neutral-900">{u.name}</td>
                <td className="px-4 py-3 text-neutral-600">{u.email}</td>
                <td className="px-4 py-3"><span className={`dos-badge ${u.role === "admin" ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-600"}`}>{u.role}</span></td>
                <td className="px-4 py-3 text-neutral-600">{u.designation || "—"}</td>
                <td className="px-4 py-3"><span className={`dos-badge ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 flex gap-1">
                  <button onClick={() => setOpen(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={14} /></button>
                  <button onClick={() => del(u.id)} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <UserModal user={open === "new" ? null : open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "user",
    designation: user?.designation || "",
    mobile: user?.mobile || "",
    department: user?.department || "",
    permissions: user?.permissions || Object.fromEntries(MODULES.map((m) => [m, Object.fromEntries(PERM_ACTIONS[m].map((a) => [a, false]))])),
    is_active: user?.is_active ?? true,
  });

  const togglePerm = (mod, act) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [mod]: { ...(f.permissions[mod] || {}), [act]: !f.permissions[mod]?.[act] } } }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (isNew) {
        await api.post("/users", form);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${user.id}`, payload);
      }
      toast.success(isNew ? "User created" : "User updated");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-2xl my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold text-neutral-900">{isNew ? "New User" : "Edit User"}</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="user-name" required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dos-input" />
            <input data-testid="user-email" required type="email" placeholder="Email" disabled={!isNew} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="dos-input disabled:bg-neutral-50" />
            <input data-testid="user-password" type="password" placeholder={isNew ? "Password" : "New password (leave blank)"} required={isNew} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="dos-input" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="dos-input">
              <option value="user">User</option><option value="admin">Admin</option>
            </select>
            <input placeholder="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="dos-input" />
            <input placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="dos-input" />
            <input placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="dos-input col-span-2" />
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Permissions</div>
            <div className="border border-neutral-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-neutral-50 text-neutral-500">
                    <th className="text-left px-3 py-2">Module</th>
                    {["view", "add", "edit", "delete", "approve"].map((a) => (<th key={a} className="px-2 py-2 text-center capitalize">{a}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((m) => (
                    <tr key={m} className="border-t border-neutral-100">
                      <td className="px-3 py-2 font-medium capitalize">{m}</td>
                      {["view", "add", "edit", "delete", "approve"].map((a) => (
                        <td key={a} className="text-center">
                          {PERM_ACTIONS[m].includes(a) ? (
                            <input type="checkbox" checked={!!form.permissions[m]?.[a]} onChange={() => togglePerm(m, a)} />
                          ) : <span className="text-neutral-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="user-save" type="submit" className="dos-btn dos-btn-primary">{isNew ? "Create User" : "Save Changes"}</button>
        </div>
      </form>
    </div>
  );
}
